"use client";

/**
 * App icon badge (Badging API).
 *
 * Every call feature-detects and swallows failures: the API is unavailable on
 * most desktop browsers, is only exposed to installed web apps on iOS/iPadOS,
 * and can reject in private windows. An unsupported platform must simply carry
 * on with no badge rather than surfacing an error.
 */

export function isBadgeSupported() {
  if (typeof navigator === "undefined") return false;
  return (
    typeof navigator.setAppBadge === "function" &&
    typeof navigator.clearAppBadge === "function"
  );
}

export async function clearAppBadge() {
  if (!isBadgeSupported()) return false;
  try {
    await navigator.clearAppBadge();
    return true;
  } catch {
    return false;
  }
}

export async function applyAppBadge(count) {
  if (!isBadgeSupported()) return false;
  const value = Number(count);
  // A zero or nonsensical count clears rather than showing an empty badge.
  if (!Number.isFinite(value) || value <= 0) return clearAppBadge();
  try {
    await navigator.setAppBadge(Math.min(Math.floor(value), 9999));
    return true;
  } catch {
    return false;
  }
}

/**
 * What the badge counts: the unread items the signed-in person can actually
 * see and clear inside YourBarrio.
 *
 *   - unread conversation messages
 *   - unread YourBarrio Support conversations
 *   - unread order notifications, for merchant-capable accounts only
 *
 * Customer-side rows in `notifications` are deliberately excluded. They are
 * written for every order status change but no customer surface lists or marks
 * them read, so counting them would strand a badge the person could never
 * clear. Keeping the badge to clearable state is what makes it agree with the
 * navbar bell and message counters.
 */
export async function fetchBadgeCount({ supabase, userId, role, includeOrderNotifications }) {
  if (!supabase || !userId) return 0;

  const messageRole = role === "business" ? "business" : "customer";
  const supportAudience = messageRole;

  const [messages, support, orders] = await Promise.all([
    supabase
      .rpc("unread_total", { p_role: messageRole, p_uid: userId })
      .then(({ data, error }) => (error ? 0 : Number(data || 0)))
      .catch(() => 0),
    supabase
      .rpc("support_unread_count_for_user", { p_audience: supportAudience })
      .then(({ data, error }) => (error ? 0 : Number(data || 0)))
      .catch(() => 0),
    includeOrderNotifications
      ? supabase
          .from("notifications")
          .select("id", { count: "exact", head: true })
          .eq("recipient_user_id", userId)
          .is("read_at", null)
          .then(({ count, error }) => (error ? 0 : Number(count || 0)))
          .catch(() => 0)
      : Promise.resolve(0),
  ]);

  const total = [messages, support, orders]
    .map((value) => (Number.isFinite(value) && value > 0 ? value : 0))
    .reduce((sum, value) => sum + value, 0);

  return total;
}
